import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigate, NavLink } from 'react-router-dom';
import { api, ApiError } from '../api';
import { can, type Me } from '../auth/me';
import { useMe } from '../auth/useMe';
import { ConflictNotice } from '../ui/ConflictNotice';
import { trapTab } from '../ui/focus';
import { DialogLayer } from '../ui/Dialog';

export const SCHEMES_RESOURCE = 'pricing.day_schemes';
export const SERVICES_RESOURCE = 'pricing.services';
export const TRANSPORT_RESOURCE = 'pricing.transport';
export const PACKAGES_RESOURCE = 'pricing.packages';
/** F26: "Bloki kosztorysów" (saved sections of a quote, with services and prices). */
export const QUOTE_BLOCKS_RESOURCE = 'quotes.blocks';
export const PRICING_KEY = ['pricing'] as const;

export const QUOTE_SETTINGS_RESOURCE = 'quotes.settings';

/** F28: the suppliers' catalogs (prices of rented gear) are a page of "Cenniki" too. */
export const SUPPLIER_PRICES_RESOURCE = 'subrentals.prices';

export const canSeePricing = (me: Me): boolean =>
  can(me, SCHEMES_RESOURCE, 'view') ||
  can(me, SERVICES_RESOURCE, 'view') ||
  can(me, PACKAGES_RESOURCE, 'view') ||
  can(me, QUOTE_BLOCKS_RESOURCE, 'view') ||
  can(me, SUPPLIER_PRICES_RESOURCE, 'view') ||
  can(me, QUOTE_SETTINGS_RESOURCE, 'view');

export interface PricingLink {
  to: string;
  label: string;
}

/**
 * The pages of "Cenniki" the role may see, in the owner's order (2026-10-22): Usługi, Transport, Schematy dni,
 * Ustawienia kosztorysu, then the rest. The first one is where `/finanse/cenniki` lands.
 */
export function pricingLinks(me: Me): PricingLink[] {
  const all: (PricingLink & { visible: boolean })[] = [
    { to: '/finanse/cenniki/uslugi', label: 'Usługi', visible: can(me, SERVICES_RESOURCE, 'view') },
    // the transport rates are fields of the two transport services
    { to: '/finanse/cenniki/transport', label: 'Transport', visible: can(me, SERVICES_RESOURCE, 'view') },
    { to: '/finanse/cenniki/schematy-dni', label: 'Schematy dni', visible: can(me, SCHEMES_RESOURCE, 'view') },
    { to: '/finanse/cenniki/ustawienia-kosztorysu', label: 'Ustawienia kosztorysu', visible: can(me, QUOTE_SETTINGS_RESOURCE, 'view') },
    { to: '/finanse/cenniki/pakiety', label: 'Pakiety', visible: can(me, PACKAGES_RESOURCE, 'view') },
    { to: '/finanse/cenniki/bloki-kosztorysow', label: 'Bloki kosztorysów', visible: can(me, QUOTE_BLOCKS_RESOURCE, 'view') },
    // owner 2026-10-21: the suppliers' catalogs live in "Katalog sprzętu"; the link stays here too
    { to: '/katalog/dostawcy', label: 'Katalogi dostawców', visible: can(me, SUPPLIER_PRICES_RESOURCE, 'view') },
  ];
  return all.filter((l) => l.visible).map(({ to, label }) => ({ to, label }));
}

/** The tabs of "Cenniki", as far as the role may see them (order: `pricingLinks`). */
export function PricingNav({ me }: { me: Me }) {
  return (
    <nav className="actions" aria-label="Cenniki">
      {pricingLinks(me).map((l) => (
        <NavLink key={l.to} to={l.to} end className={({ isActive }) => `btn small${isActive ? ' primary' : ''}`}>
          {l.label}
        </NavLink>
      ))}
    </nav>
  );
}

/** `/finanse/cenniki`: the first tab the role may see. */
export function PricingIndex() {
  const { me } = useMe();
  if (!me) return null;
  const first = pricingLinks(me)[0];
  return <Navigate to={first ? first.to : '/'} replace />;
}

/** Confirmation before a scheme or a service is deleted (409 and 422 shown in place). */
export function DeleteDialog({ title, name, url, onClose }: { title: string; name: string; url: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api(url, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PRICING_KEY });
      onClose();
    },
  });
  const error = remove.error instanceof ApiError ? remove.error : null;
  return (
    <div className="dialog-backdrop">
      <div
        className="dialog panel"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-title"
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
          <DialogLayer onClose={onClose} busy={remove.isPending} />
        <h2 id="delete-title">{title}</h2>
        <p>„{name}” zniknie z cennika. Kosztorysy, które już go używają, zostają bez zmian, a w historii zmian zostaje wpis o usunięciu.</p>
        {error?.status === 409 && (
          <ConflictNotice
            onReload={() => {
              void queryClient.invalidateQueries({ queryKey: PRICING_KEY });
              onClose();
            }}
          />
        )}
        {error && error.status !== 409 && (
          <p className="alert" role="alert">
            {error.message}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="button" autoFocus disabled={remove.isPending} onClick={() => remove.mutate()}>
            {remove.isPending ? 'Usuwanie…' : 'Usuń'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

/** Per-field message of a validation error (path may be `pctsBp.1`). */
export function issueFor(error: unknown, path: string): string | null {
  if (!(error instanceof ApiError) || !Array.isArray(error.details)) return null;
  const hit = (error.details as { path?: unknown; message?: unknown }[]).find((d) => typeof d.path === 'string' && (d.path === path || d.path.startsWith(`${path}.`)));
  return typeof hit?.message === 'string' ? hit.message : null;
}

/** An error not tied to one field (403, 422, network…); 409 has its own notice. */
export function GeneralError({ error, fields }: { error: unknown; fields: readonly string[] }) {
  if (!(error instanceof ApiError) || error.status === 409) return null;
  if (fields.some((f) => issueFor(error, f))) return null;
  return (
    <p className="alert" role="alert">
      {error.message}
    </p>
  );
}
